/* ==========================================================================
   lumi — behaviour.                                        assets/theme.js

   Built on two vendored libraries rather than hand-rolled equivalents:

     Motion (MIT, motion.dev)  — inView / animate / stagger / scroll.
       Replaces a bespoke IntersectionObserver + rAF loop. It composes
       independent transforms (translate, scale, rotate) without the
       clobbering that plain `transform` strings cause, drives the Web
       Animations API off the main thread where the browser allows, and
       handles the reduced-motion and interrupt cases properly.

     Lenis (MIT, darkroom.engineering) — damped scrolling. This is the
       single largest contributor to the "expensive" feel on studio sites:
       the page carries momentum instead of snapping a fixed number of
       pixels per wheel tick. Off by default under reduced-motion, and
       disableable from the theme editor.

   Both are self-hosted in assets/. No build step, no CDN request.
   Everything degrades: if either global is missing this file still runs.
   ========================================================================== */
(function () {
  'use strict';

  var doc  = document;
  var root = doc.documentElement;
  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var RM = reduceMQ.matches;

  var M = window.Motion || null;          /* may be absent — always guard */
  var hasMotion = !!(M && M.animate && M.inView);

  function each(l, f) { Array.prototype.forEach.call(l, f); }
  function $(s, c) { return (c || doc).querySelectorAll(s); }

  /* lumi's easing, expressed for Motion. Same curves as the stylesheet. */
  var EASE = {
    set:  [0.19, 1, 0.22, 1],
    rise: [0.22, 0.61, 0.36, 1],
    draw: [0.32, 0.72, 0, 1]
  };

  /* ===================================================== 1. DOM PREP ===== */

  function bindShots(scope) {
    each($('figure .shot', scope), function (img) {
      if (img.__lumi) return; img.__lumi = 1;
      var done = function () {
        if (img.naturalWidth > 1) img.closest('figure').classList.add('has-shot');
      };
      if (img.complete) done();
      else {
        img.addEventListener('load', done);
        img.addEventListener('error', function () { img.remove(); });
      }
    });
  }

  function dupMarquee(scope) {
    each($('.marquee .track', scope), function (t) {
      if (t.__lumi) return; t.__lumi = 1;
      t.innerHTML = t.innerHTML + t.innerHTML;   /* so translateX(-50%) loops */
    });
  }

  try { bindShots(doc); dupMarquee(doc); } catch (e) {}

  /* ============================================ 2. WORD SPLIT (display) ===
     Kept hand-written: Motion has no splitter, and this one walks TEXT
     NODES ONLY so <br>, <em> and Liquid's richtext <span>s all survive.
     Never per-character — that destroys the kerning Cormorant is for. */

  function split(el) {
    if (el.__w) return el.__w;
    var walk = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    var texts = [], n, words = [];
    while ((n = walk.nextNode())) if (n.nodeValue.trim()) texts.push(n);
    texts.forEach(function (node) {
      var frag = doc.createDocumentFragment();
      node.nodeValue.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(doc.createTextNode(part)); return; }
        var mask = doc.createElement('span'); mask.className = 'lw';
        var word = doc.createElement('i');    word.textContent = part;
        mask.appendChild(word); frag.appendChild(mask); words.push(word);
      });
      node.parentNode.replaceChild(frag, node);
    });
    el.__w = words; return words;
  }

  /* group words into visual lines so a whole line rises as one object */
  function lines(el) {
    var w = el.__w || [];
    if (!w.length) return [];
    var tol = Math.max(4, parseFloat(getComputedStyle(el).fontSize) * 0.45);
    var tops = w.map(function (x) { return x.parentNode.offsetTop; });
    var out = [], cur = [], last = null;
    for (var i = 0; i < w.length; i++) {
      if (last !== null && Math.abs(tops[i] - last) > tol) { out.push(cur); cur = []; }
      cur.push(w[i]); last = tops[i];
    }
    if (cur.length) out.push(cur);
    return out;
  }

  /* ================================================= 3. REVEAL / ENTRANCE ==
     Motion's inView replaces the observer + threshold bookkeeping. One-shot
     by design: this page is a printed sequence and ink does not un-set. */

  /* The ENTRANCE stays on CSS transitions, deliberately.
     Motion drives the Web Animations API, and WAAPI reverts an element to its
     underlying CSS value when the animation finishes. Because the at-rest CSS
     here IS the hidden state, a Motion-driven entrance snapped every word back
     to hidden the instant it landed. CSS owns entrance; Motion owns the things
     CSS cannot do well: scroll progress and interruptible interaction. */
  function playSet(el) {
    var ls = lines(el);
    var gap = 78, t = 0;
    ls.forEach(function (line, i) {
      if (i) { t += gap; gap *= 0.86; }
      line.forEach(function (w) {
        w.style.setProperty('--wd', Math.round(Math.min(t, 300)) + 'ms');
      });
    });
    el.classList.add('is-split');
    /* Force a synchronous style flush so the transition has a start value.
       NOT requestAnimationFrame: rAF never fires in a background tab, which
       would leave the headline permanently hidden. */
    void el.offsetWidth;
    el.classList.add('in');
  }

  function playRise(el) { el.classList.add('in'); }

  function reveal(el) {
    if (el.__played) return; el.__played = true;
    if (el.classList.contains('set')) playSet(el); else playRise(el);
  }

  /* decaying stagger — one gesture with an accent on the first item, rather
     than four metronome beats. Stamped as data-d so CSS and JS agree. */
  function stagger(scope) {
    each($('[data-stagger]', scope), function (group) {
      var cfg = group.getAttribute('data-stagger').split(',');
      var base = parseFloat(cfg[0]) || 90;
      var decay = cfg.length > 1 ? parseFloat(cfg[1]) : 0.8;
      var t = parseFloat(group.getAttribute('data-d') || 0), gap = base;
      each(group.children, function (child, i) {
        if (i) { t += gap; gap *= decay; }
        child.dataset.d = Math.round(t);
        child.style.setProperty('--d', Math.round(t) + 'ms');
      });
    });
  }

  /* ============================================== 4. SCROLL-LINKED LAYER ===
     Motion's scroll() gives a 0→1 progress per element without a bespoke
     rAF loop. Writes ONLY custom properties; the stylesheet decides what
     each section does with --p. */

  function bindScrollProgress() {
    if (!hasMotion || RM || !M.scroll) return;
    each($('.hero,.bleed,.story,.layer .stage,.features,.specsec,.pdp,.ivp'), function (el) {
      M.scroll(
        function (p) { el.style.setProperty('--p', Math.round(p * 1000) / 1000); },
        { target: el, offset: ['start end', 'end start'] }
      );
    });
  }

  /* ==================================================== 5. SMOOTH SCROLL ===
     Lenis. Gated on a theme setting and on reduced-motion, and skipped on
     touch, where the native momentum is already correct and overriding it
     is the fastest way to make a phone feel broken. */

  var lenis = null;
  function initLenis() {
    if (RM || !window.Lenis) return;
    if (root.dataset.smooth === 'off') return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    lenis = new window.Lenis({
      duration: 1.05,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      syncTouch: false
    });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);

    /* in-page anchors go through Lenis so they are damped too */
    each($('a[href^="#"]'), function (a) {
      a.addEventListener('click', function (e) {
        var id = a.getAttribute('href');
        if (id.length < 2) return;
        var t = doc.querySelector(id);
        if (!t) return;
        e.preventDefault();
        lenis.scrollTo(t, { offset: -100 });
      });
    });
  }

  /* ============================================================ 6. NAV ==== */

  function bindNav() {
    var nav = doc.querySelector('.nav');
    if (!nav) return;
    var on = null;
    var set = function (y) {
      var next = y > 40;
      if (next !== on) { on = next; nav.classList.toggle('scrolled', next); }
    };
    set(window.scrollY);
    if (lenis) lenis.on('scroll', function (e) { set(e.scroll); });
    else window.addEventListener('scroll', function () { set(window.scrollY); }, { passive: true });
  }

  /* =========================================================== 7. BOOT ==== */

  function prepare(scope) {
    stagger(scope);
    each($('[data-d]', scope), function (el) {
      el.style.setProperty('--d', parseFloat(el.getAttribute('data-d')) + 'ms');
    });
    each($('.reveal.set', scope), split);          /* split while still hidden */
  }

  function observe(scope) {
    each($('.reveal', scope), function (el) {
      if (el.__bound) return; el.__bound = true;
      if (el.closest('[data-overture]')) return;   /* scheduled, not scrolled */
      if (hasMotion && !RM) {
        M.inView(el, function () { reveal(el); }, { margin: '0px 0px -12% 0px' });
      } else {
        reveal(el);
      }
    });
  }

  function boot() {
    try {
      prepare(doc);

      if (RM || !hasMotion) {
        each($('.reveal'), function (el) { el.classList.add('is-split', 'in'); });
      } else {
        observe(doc);
        /* the overture is scheduled, not scrolled. rAF never fires in a
           background tab, so race it against a timer or a page opened in a
           background tab shows an empty hero. */
        var played = false;
        var overture = function () {
          if (played) return; played = true;
          each($('[data-overture] .reveal, [data-overture].reveal'), reveal);
        };
        requestAnimationFrame(function () { requestAnimationFrame(overture); });
        setTimeout(overture, 400);
      }

      initLenis();
      bindNav();
      bindScrollProgress();
    } catch (err) {
      root.classList.add('no-motion');
      if (window.console) console.warn('[lumi] init failed:', err);
    }
    clearTimeout(window.__lumiWD);
  }

  /* Cormorant decides where the hero's lines break, so measure after it
     lands, but never hold the page hostage to a font CDN. Hard 250ms cap. */
  var fonts = (doc.fonts && doc.fonts.load)
    ? Promise.all([doc.fonts.load('500 1em Cormorant'), doc.fonts.load('italic 400 1em Cormorant')])
    : Promise.resolve();
  Promise.race([fonts, new Promise(function (r) { setTimeout(r, 250); })]).then(boot, boot);

  /* ====================================================== 8. LISTENERS ==== */

  doc.addEventListener('visibilitychange', function () {
    root.classList.toggle('tab-hidden', doc.hidden);
  });

  /* Shopify's editor swaps sections in and out live */
  doc.addEventListener('shopify:section:load', function (e) {
    try {
      bindShots(e.target); dupMarquee(e.target); prepare(e.target);
      if (RM || !hasMotion) each($('.reveal', e.target), function (el) { el.classList.add('is-split','in'); });
      else observe(e.target);
    } catch (err) {}
  });

  function onPref() {
    if (!reduceMQ.matches || RM) return;
    RM = true;
    if (lenis) { lenis.destroy(); lenis = null; }
    each($('.reveal'), function (el) { el.classList.add('is-split', 'in'); });
  }
  if (reduceMQ.addEventListener) reduceMQ.addEventListener('change', onPref);
  else if (reduceMQ.addListener) reduceMQ.addListener(onPref);

  /* ============================================ 9. COMMERCE (own scope) ===
     Last and independently guarded: a motion bug must never stop someone
     buying the skirt. */
  try {
    var live = doc.getElementById('lumi-live');
    function say(m) { if (live) live.textContent = m; }

    var field = doc.querySelector('.news-field');
    if (field) {
      var nform = field.closest('form');
      if (nform) {
        if (nform.querySelector('.form-error')) field.classList.add('has-error');
        nform.addEventListener('submit', function () {
          var input = field.querySelector('input[type=email]');
          if (input && !input.checkValidity()) {
            field.classList.remove('has-error'); void field.offsetWidth;
            field.classList.add('has-error');
          }
        });
        field.addEventListener('input', function () { field.classList.remove('has-error'); });
      }
    }

    var form = doc.querySelector('form.buy-form');
    if (form) {
      var hidden = form.querySelector('input[name="id"]');
      var addBtn = form.querySelector('[data-add]');
      var picked = doc.querySelector('[data-size-picked]');
      var group  = form.querySelector('.sizes');
      var btns   = Array.prototype.slice.call(form.querySelectorAll('.size'));

      if (group) { group.setAttribute('role', 'group'); group.setAttribute('aria-label', 'Size'); }

      btns.forEach(function (btn, i) {
        btn.type = 'button';
        btn.setAttribute('aria-pressed', btn.classList.contains('is-active') ? 'true' : 'false');
        btn.tabIndex = btn.classList.contains('is-active') || (i === 0 && !form.querySelector('.size.is-active')) ? 0 : -1;

        btn.addEventListener('pointerdown', function (e) {
          var r = btn.getBoundingClientRect();
          var pct = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
          btn.style.setProperty('--fx-origin', pct.toFixed(1) + '%');
        });

        btn.addEventListener('click', function () {
          if (btn.hasAttribute('disabled')) return;
          btns.forEach(function (b) {
            b.classList.remove('is-active'); b.setAttribute('aria-pressed', 'false'); b.tabIndex = -1;
          });
          btn.classList.add('is-active'); btn.setAttribute('aria-pressed', 'true'); btn.tabIndex = 0;
          if (hidden) hidden.value = btn.dataset.variant;
          if (picked) {
            if (hasMotion && !RM) {
              M.animate(picked, { opacity: [1, 0], y: [0, -4] }, { duration: 0.17, easing: EASE.rise })
               .finished.then(function () {
                 picked.textContent = btn.textContent.trim();
                 M.animate(picked, { opacity: [0, 1], y: [4, 0] }, { duration: 0.22, easing: EASE.set });
               });
            } else {
              picked.textContent = btn.textContent.trim();
            }
          }
          if (addBtn) { addBtn.removeAttribute('disabled'); addBtn.classList.remove('is-added', 'is-adding'); }
        });

        btn.addEventListener('keydown', function (e) {
          var d = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1
                : (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')  ? -1 : 0;
          if (!d) return;
          e.preventDefault();
          var start = btns.indexOf(btn), j = start, n = btns.length;
          do { j = (j + d + n) % n; } while (btns[j].hasAttribute('disabled') && j !== start);
          btns[j].focus(); btns[j].click();
        });
      });

      form.addEventListener('submit', function (e) {
        if (!hidden || !hidden.value) return;
        e.preventDefault();
        var size = form.querySelector('.size.is-active');
        addBtn.classList.add('is-adding');
        addBtn.setAttribute('disabled', '');
        say('Adding to bag.');

        var rootUrl = (window.Shopify && window.Shopify.routes) ? window.Shopify.routes.root : '/';
        fetch(rootUrl + 'cart/add.js', {
          method: 'POST', body: new FormData(form), headers: { 'Accept': 'application/json' }
        })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
        .then(function () { return fetch(rootUrl + 'cart.js'); })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          addBtn.classList.remove('is-adding');
          addBtn.classList.add('is-added');
          var bag = doc.querySelector('.bag-count');
          if (bag) {
            bag.textContent = '(' + cart.item_count + ')';
            if (hasMotion && !RM) {
              M.animate(bag, { scale: [1, 1.28, 1] }, { duration: 0.6, easing: EASE.draw });
            }
          }
          say('Added to bag' + (size ? ', size ' + size.textContent.trim() : '') +
              '. ' + cart.item_count + ' in bag.');
          setTimeout(function () {
            addBtn.classList.remove('is-added');
            addBtn.removeAttribute('disabled');
          }, 1500);
        })
        .catch(function () {
          addBtn.classList.remove('is-adding');
          addBtn.removeAttribute('disabled');
          say('Could not add to bag. Please try again.');
        });
      });
    }
  } catch (e) { if (window.console) console.warn('[lumi] commerce init failed:', e); }
})();
